import {
	type AdjustableValuesUpdate,
	Currency,
	type FinancialPeriod,
	Formula,
} from "@inrev/common";
import { DateTime } from "luxon";

type LeafView = FinancialPeriod.LeafView;
type LineItemView = FinancialPeriod.LineItemView;
type AdjustedLeaf = FinancialPeriod.AdjustedLeaf;
type LineItemGroupView = FinancialPeriod.LineItemGroupView;

export const applyValuesToLeafLike = <T extends LeafView>(
	item: T,
	values: AdjustableValuesUpdate,
): T => {
	if (values.value === null) {
		const { given: _, adjusted: __, ...rest } = item;
		return { ...rest, allowed: undefined } as T;
	}
	if (values.value !== undefined) {
		const newGiven = values.value;
		// An incoming formula (e.g. a created line item with both a given and an
		// adjustment) takes precedence; otherwise re-evaluate any existing formula
		// against the new given. `formula: null` clears the adjustment.
		const formula =
			values.formula === null ? undefined : (values.formula ?? item.adjusted?.formula);
		const newAdjusted: AdjustedLeaf | undefined = formula
			? {
					value: Currency.round(Formula.evaluate(newGiven, formula)),
					formula,
					createdByUserId: item.adjusted?.createdByUserId ?? null,
					createdAt: item.adjusted?.createdAt ?? DateTime.now(),
					...(item.adjusted?.note !== undefined ? { note: item.adjusted.note } : {}),
				}
			: undefined;
		const allowed = newAdjusted != null ? Currency.add(newGiven, newAdjusted.value) : newGiven;
		return { ...item, given: newGiven, adjusted: newAdjusted, allowed };
	}
	if (values.formula === null) {
		const { adjusted: _, ...rest } = item;
		return { ...rest, allowed: rest.given } as T;
	}
	if (values.formula !== undefined) {
		const formula = values.formula;
		const computedValue = Currency.round(Formula.evaluate(item.given ?? 0, formula));
		const adjusted: AdjustedLeaf = {
			value: computedValue,
			formula,
			createdByUserId: item.adjusted?.createdByUserId ?? null,
			createdAt: item.adjusted?.createdAt ?? DateTime.now(),
			...(item.adjusted?.note !== undefined ? { note: item.adjusted.note } : {}),
		};
		const allowed = Currency.add(item.given ?? Currency.ZERO, computedValue);
		return { ...item, adjusted, allowed };
	}
	return item;
};

export const patchLineItemGroupView = (
	group: LineItemGroupView,
	label: string,
	values: AdjustableValuesUpdate,
): LineItemGroupView => {
	if (values.value === null) {
		return {
			...group,
			lineItems: group.lineItems.filter((item) => item.label !== label),
		};
	}

	const newItem: LineItemView = { label };
	const exists = group.lineItems.some((item) => item.label === label);
	const lineItems = exists
		? group.lineItems.map((item) =>
				item.label === label ? applyValuesToLeafLike(item, values) : item,
			)
		: [...group.lineItems, applyValuesToLeafLike(newItem, values)];
	return { ...group, lineItems };
};

export const renameLineItemGroupViewLabel = (
	group: LineItemGroupView,
	oldLabel: string,
	newLabel: string,
): LineItemGroupView => {
	const hasOldLabel = group.lineItems.some((item) => item.label === oldLabel);
	const hasNewLabel = group.lineItems.some((item) => item.label === newLabel);
	if (!hasOldLabel || hasNewLabel) {
		return group;
	}
	return {
		...group,
		lineItems: group.lineItems.map((item) =>
			item.label === oldLabel ? { ...item, label: newLabel } : item,
		),
	};
};
